// =====================================================================================
// VideoStateManager - Manages video-related state
// =====================================================================================
// Centralizes state management for video connections, participants, and room state
// Provides a clean interface for state updates and queries

import type { Room } from 'livekit-client';
import type {
  IVideoStateManager,
  RoomState,
  ConnectionState
} from './types.js';

export class VideoStateManager implements IVideoStateManager {
  private state: RoomState & ConnectionState = {
    // Room state
    room: null,
    participants: 0,
    currentPrivateAreaId: null,

    // Connection state
    isConnected: false,
    isReconnecting: false,
    connectionAttempts: 0,
    currentRequestId: null,
    connectionInProgress: null
  };

  // Optional state change callbacks
  public onStateChange?: (state: RoomState & ConnectionState) => void;

  constructor() {
    console.info('🎥 VideoStateManager initialized');
  }

  getState(): RoomState & ConnectionState {
    return { ...this.state };
  }

  setConnected(isConnected: boolean): void {
    this.updateState({ isConnected });
    if (!isConnected) {
      // Reset connection tracking when disconnected
      this.updateState({
        connectionInProgress: null,
        currentRequestId: null
      });
    }
  }

  setReconnecting(isReconnecting: boolean): void {
    this.updateState({ isReconnecting });
  }

  setParticipants(count: number): void {
    this.updateState({ participants: count });
  }

  setCurrentPrivateAreaId(areaId: string | null): void {
    this.updateState({ currentPrivateAreaId: areaId });
    console.info('🎥 Current private area updated:', areaId);

    // Clear connection tracking if leaving area
    if (!areaId) {
      this.updateState({
        connectionInProgress: null,
        currentRequestId: null
      });
    }
  }

  incrementConnectionAttempts(): number {
    const newAttempts = this.state.connectionAttempts + 1;
    this.updateState({ connectionAttempts: newAttempts });
    return newAttempts;
  }

  resetConnectionAttempts(): void {
    this.updateState({ connectionAttempts: 0 });
  }

  setConnectionInProgress(areaId: string | null): void {
    this.updateState({ connectionInProgress: areaId });
    if (areaId) {
      console.info('🎥 Setting connection in progress for area:', areaId);
    }
  }

  setCurrentRequestId(requestId: string | null): void {
    this.updateState({ currentRequestId: requestId });
    if (requestId) {
      console.debug('🎥 Current request ID set:', requestId);
    }
  }

  setRoom(room: Room | null): void {
    this.updateState({ room });
    if (room) {
      // Update participants count when room is set
      this.updateState({ participants: room.remoteParticipants?.size || 0 });
    } else {
      // Reset participants when room is cleared
      this.updateState({ participants: 0 });
    }
  }

  // Helper method to check if can connect to a specific area
  canConnectToArea(areaId: string): boolean {
    return (
      this.state.currentPrivateAreaId === areaId &&
      !this.state.isConnected &&
      !this.state.isReconnecting
      // Removed connectionInProgress check - it's ok to try connecting to the same area
    );
  }

  // Helper method to check if should handle token for request
  shouldHandleToken(requestId?: string): boolean {
    if (!this.state.currentRequestId || !this.state.connectionInProgress) {
      return false;
    }

    if (requestId && requestId !== this.state.currentRequestId) {
      console.warn('🎥 Token request ID mismatch:', {
        received: requestId,
        expected: this.state.currentRequestId
      });
      return false;
    }

    return true;
  }

  // Reset all state
  reset(): void {
    this.state = {
      room: null,
      participants: 0,
      currentPrivateAreaId: null,
      isConnected: false,
      isReconnecting: false,
      connectionAttempts: 0,
      currentRequestId: null,
      connectionInProgress: null
    };
    this.onStateChange?.(this.state);
  }

  private updateState(partialState: Partial<RoomState & ConnectionState>): void {
    this.state = { ...this.state, ...partialState };
    this.onStateChange?.(this.state);
  }
}